// User action logger to track recent actions before crashes
import { redactSensitiveUrl } from 'helpers/redactSensitiveUrl';

const MAX_ACTIONS = 50; // Maximum number of actions to store
let actionHistory = [];
let isInitialized = false;

// Persistence keys. STORAGE_KEY mirrors the live in-memory history; on the next
// page load it is rotated into PREV_STORAGE_KEY so a crash+reload preserves the
// actions that led up to the crash (the in-memory copy is wiped by the reload).
const STORAGE_KEY = 'cg-action-history';
const PREV_STORAGE_KEY = 'cg-action-history-prev';
let persistEnabled = false;
let persistTimer = null;

// Action types with more specific categories
const ACTION_TYPES = {
  // User interaction events
  UI_INTERACTION: 'ui-interaction', // Clicks, form submissions, etc.
  INPUT: 'input', // Input changes, keystrokes

  // Navigation events
  ROUTE_CHANGE: 'route-change', // React router navigation
  HISTORY_CHANGE: 'history-change', // Browser history API

  // API calls
  API_REQUEST: 'api-request', // External API calls (axios, fetch)
  INTERNAL_REQUEST: 'internal-request', // Internal app requests

  // Other
  CUSTOM: 'custom', // Custom logged actions
  ERROR: 'error', // Non-fatal errors
  LIFECYCLE: 'lifecycle', // Component lifecycle events
};

// DOM events to track
const TRACKED_DOM_EVENTS = ['click', 'submit', 'change', 'input', 'keydown'];

// ── Persistence ──────────────────────────────────────────────────────────────
// The in-memory history is lost when a crash forces a reload — exactly when it's
// most needed. When debug capture is enabled (local dev, DEV_WITH_LOGGING, or
// the `cg-debug-tools` localStorage flag in any non-PROD build) we mirror the
// history to localStorage, flush it the moment an uncaught error fires, and
// rotate it on the next load so the pre-crash actions can be inspected.
// Deliberately a no-op (zero overhead) in PROD.
function shouldPersist() {
  try {
    if (process.env.NODE_ENV === 'development') return true;
    if (process.env.REACT_APP_ENV_MODE === 'DEV_WITH_LOGGING') return true;
    return (
      process.env.REACT_APP_ENV_MODE !== 'PROD' &&
      typeof window !== 'undefined' &&
      window.localStorage.getItem('cg-debug-tools') === '1'
    );
  } catch {
    return false;
  }
}

function safeRead(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // storage full / blocked — keep the in-memory copy, nothing else to do
  }
}

// Synchronous flush — used on uncaught errors and on unload so the very last
// actions before a crash/reload are captured.
function flushPersistNow() {
  if (!persistEnabled) return;
  safeWrite(STORAGE_KEY, JSON.stringify(actionHistory));
}

// Throttled flush for the steady stream of normal UI events.
function schedulePersist() {
  if (!persistEnabled || persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    flushPersistNow();
  }, 300);
}

// Initialize the action logger
export function initActionLogger() {
  if (isInitialized) return;

  persistEnabled = shouldPersist();
  if (persistEnabled) {
    // Rotate the prior session's history into the "previous" slot so a
    // crash+reload preserves the pre-crash actions, then start a fresh session.
    const prior = safeRead(STORAGE_KEY);
    if (prior && prior !== '[]') safeWrite(PREV_STORAGE_KEY, prior);
    safeWrite(STORAGE_KEY, '[]');

    // Crashes are recorded via logCrash(), called from the central logRumError
    // handler (React error boundary + window.onerror + unhandled rejections),
    // which gives us the error stack AND the React component stack.
    // Flush on navigation/reload so the latest in-memory actions survive.
    window.addEventListener('pagehide', flushPersistNow);
    window.addEventListener('beforeunload', flushPersistNow);
  }

  // Track DOM events
  TRACKED_DOM_EVENTS.forEach((eventType) => {
    document.addEventListener(
      eventType,
      (e) => {
        const target = e.target;
        const nodeName = target.nodeName.toLowerCase();
        const id = target.id ? `#${target.id}` : '';
        const classes =
          target.className && typeof target.className === 'string'
            ? `.${target.className.split(' ').join('.')}`
            : '';
        const text = target.innerText
          ? target.innerText.length > 20
            ? `${target.innerText.substring(0, 20)}...`
            : target.innerText
          : '';

        let actionDescription = `${eventType} on ${nodeName}${id}${classes}`;
        if (text) actionDescription += ` ("${text}")`;

        // Categorize the event
        let actionType = ACTION_TYPES.UI_INTERACTION;
        if (['input', 'change', 'keydown'].includes(eventType)) {
          actionType = ACTION_TYPES.INPUT;
        }

        logAction(actionType, actionDescription);
      },
      true,
    );
  });

  // Track navigation changes (History API)
  const originalPushState = window.history.pushState;
  window.history.pushState = function () {
    const path = redactSensitiveUrl(arguments[2]);
    logAction(ACTION_TYPES.HISTORY_CHANGE, `Navigated to: ${path}`);
    return originalPushState.apply(this, arguments);
  };

  window.addEventListener('popstate', () => {
    logAction(
      ACTION_TYPES.HISTORY_CHANGE,
      `Navigated to: ${redactSensitiveUrl(window.location.pathname)}`,
    );
  });

  // Track fetch API
  const originalFetch = window.fetch;
  window.fetch = function () {
    const url = arguments[0];
    const method = arguments[1]?.method || 'GET';

    // Determine if this is an internal or external request
    const isExternal = isExternalUrl(url);
    const actionType = isExternal ? ACTION_TYPES.API_REQUEST : ACTION_TYPES.INTERNAL_REQUEST;

    logAction(actionType, `${method} ${redactSensitiveUrl(url)}${isExternal ? ' (external)' : ''}`);

    return originalFetch.apply(this, arguments);
  };

  // Track XMLHttpRequest
  const originalXhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function () {
    const method = arguments[0];
    const url = arguments[1];

    // Store the URL to use in the loadend event
    this._url = url;
    this._method = method;

    this.addEventListener('loadend', () => {
      // Determine if this is an internal or external request
      const isExternal = isExternalUrl(this._url);
      const actionType = isExternal ? ACTION_TYPES.API_REQUEST : ACTION_TYPES.INTERNAL_REQUEST;

      logAction(
        actionType,
        `XHR ${this._method} ${redactSensitiveUrl(this._url)} (${this.status})${isExternal ? ' (external)' : ''}`,
      );
    });

    return originalXhrOpen.apply(this, arguments);
  };

  // Check for axios if available
  if (window.axios) {
    const originalAxiosRequest = window.axios.request;
    window.axios.request = function (config) {
      const method = config.method?.toUpperCase() || 'GET';
      const url = config.url;

      // Determine if this is an internal or external request
      const isExternal = isExternalUrl(url);
      const actionType = isExternal ? ACTION_TYPES.API_REQUEST : ACTION_TYPES.INTERNAL_REQUEST;

      logAction(actionType, `AXIOS ${method} ${url}${isExternal ? ' (external)' : ''}`);

      return originalAxiosRequest.apply(this, arguments);
    };
  }

  isInitialized = true;
}

// Helper to determine if a URL is external
function isExternalUrl(url) {
  if (!url) return false;

  // If it's a relative URL, it's internal
  // if (url.startsWith("/") && !url.startsWith("//")) return false;

  try {
    // Check if the URL has a different origin than the current page
    const currentOrigin = window.location.origin;
    const urlOrigin = new URL(url, window.location.origin).origin;
    return currentOrigin !== urlOrigin;
  } catch (e) {
    // If URL parsing fails, assume it's internal
    return false;
  }
}

// Log a user action. `meta` carries optional extra detail (e.g. a crash's
// { stack, componentStack, source }) surfaced in the Action History viewer.
export function logAction(type, description, meta = null) {
  const timestamp = new Date().toISOString();
  const entry = { timestamp, type, description };
  if (meta) entry.meta = meta;
  actionHistory.push(entry);

  // Keep only the most recent actions
  if (actionHistory.length > MAX_ACTIONS) {
    actionHistory.shift();
  }

  schedulePersist();
}

// Record a crash into the action history with its stack trace and React
// component stack, then flush immediately so it survives the reload. Called
// from logRumError (the React error boundary, window.onerror, and unhandled
// rejections all funnel through there). Never throws.
export function logCrash(error, info = {}) {
  try {
    const message = (error && (error.message || String(error))) || 'Unknown error';
    const source = info.source || 'error';
    logAction(ACTION_TYPES.ERROR, `Crash (${source}): ${message}`, {
      stack: (error && error.stack) || null,
      componentStack: info.componentStack || null,
      source,
    });
    flushPersistNow();
  } catch {
    // crash logging must never itself cause a crash
  }
}

// Get the action history
export function getActionHistory() {
  return [...actionHistory];
}

// Get the action history captured by the previous page session — i.e. the
// actions right before the last reload/crash. Empty array if none.
export function getPreviousSessionHistory() {
  try {
    return JSON.parse(safeRead(PREV_STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

// Clear the action history
export function clearActionHistory() {
  actionHistory = [];
  if (persistEnabled) safeWrite(STORAGE_KEY, '[]');
}

// Clear both the live and previous-session persisted histories.
export function clearPersistedHistory() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(PREV_STORAGE_KEY);
  } catch {
    // ignore
  }
}

// Log a custom action (for manual tracking)
export function logCustomAction(description) {
  logAction(ACTION_TYPES.CUSTOM, description);
}

// Log a lifecycle event
export function logLifecycleEvent(component, event) {
  logAction(ACTION_TYPES.LIFECYCLE, `${component}: ${event}`);
}

// Export the action types for use in other components
export const ACTION_CATEGORIES = ACTION_TYPES;
